import React from 'react';
import { Link } from 'react-router-dom';
import { collection, getDocs, doc } from 'firebase/firestore';
import { initializeApp, getApps } from 'firebase/app';
import { getAuth, createUserWithEmailAndPassword, signOut } from 'firebase/auth';
import { setDoc, updateDoc, deleteDoc } from '../../api/FirestoreInterceptor';
import { db, firebaseConfig } from '../../config-firebase/firebase';
import { GlobalContext } from '../../GlobalContext';
import { logAction } from '../../api/AuditLogger';
import styles from './PermissionsManagement.module.scss';

const MODULES = [
  { id: 'category', name: 'Adicionar Categoria' },
  { id: 'editButtonCat', name: 'Editar Categorias' },
  { id: 'item', name: 'Adicionar Prato' },
  { id: 'editButtonDishes', name: 'Editar Pratos' },
  { id: 'sidedishes', name: 'Adicionar Acompanhamento' },
  { id: 'editButtonSidedishes', name: 'Editar Acompanhamentos' },
  { id: 'customer', name: 'Lista de Clientes' },
  { id: 'promotions', name: 'Promoções' },
  { id: 'stock', name: 'Estoque' },
  { id: 'supplies', name: 'Insumos' },
  { id: 'managementRecipes', name: 'Receitas' },
  { id: 'expenses', name: 'Despesas' },
  { id: 'request', name: 'Vendas' },
  { id: 'sell-flow', name: 'Fechamento de Caixa' },
  { id: 'operationCost', name: 'Custo de Operações' },
  { id: 'financial-summary', name: 'Resumo Financeiro' },
  { id: 'excel-management', name: 'Carga em Massa (Excel)' },
  { id: 'welcome', name: 'Mensagens do Sistema' },
  { id: 'frontimage', name: 'Configurações do Terminal' },
  { id: 'styles', name: 'Marca e Estilo' },
  { id: 'requestlist', name: 'PDV' },
  { id: 'kitchen', name: 'Cozinha' },
  { id: 'requestlistcheck', name: 'Lista de Pedidos' },
  { id: 'cancelOrder', name: 'Cancelar Pedidos' }
];

export default function PermissionsManagement() {
  const { currentUser } = React.useContext(GlobalContext);
  const [users, setUsers] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [savingId, setSavingId] = React.useState(null);
  const [message, setMessage] = React.useState(null);

  // Estado para o Popup/Modal de Cadastro de Novo Administrador
  const [showCreateModal, setShowCreateModal] = React.useState(false);
  const [formName, setFormName] = React.useState('');
  const [formEmail, setFormEmail] = React.useState('');
  const [formPassword, setFormPassword] = React.useState('');
  const [showFormPassword, setShowFormPassword] = React.useState(false);
  const [formPermissions, setFormPermissions] = React.useState(['requestlist']);
  const [createLoading, setCreateLoading] = React.useState(false);
  const [createError, setCreateError] = React.useState(null);

  const togglePermissionInCreate = (permId) => {
    setFormPermissions((prev) =>
      prev.includes(permId)
        ? prev.filter((p) => p !== permId)
        : [...prev, permId]
    );
  };

  const handleSelectAllPermissionsInCreate = () => {
    setFormPermissions(MODULES.map((m) => m.id));
  };

  const handleClearAllPermissionsInCreate = () => {
    setFormPermissions([]);
  };

  // Estado para o Popup/Modal de Edição de Administrador
  const [editingUser, setEditingUser] = React.useState(null);
  const [editLoading, setEditLoading] = React.useState(false);
  const [editError, setEditError] = React.useState(null);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const querySnapshot = await getDocs(collection(db, 'admins'));
      const list = [];
      querySnapshot.forEach((docSnapshot) => {
        list.push({ id: docSnapshot.id, ...docSnapshot.data() });
      });
      setUsers(list);
    } catch (error) {
      console.error('Erro ao buscar administradores:', error);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    fetchUsers();
  }, []);

  // Encontra qual e-mail possui Controle Total na tabela
  const currentTotalAdmin = users.find(u => u.role === 'admin_TOTAL');

  // Adiciona permissão inline
  const handleAddPermission = (userId, value) => {
    setUsers(prev => prev.map(user => {
      if (user.id !== userId) return user;

      if (value === 'admin_TOTAL') {
        return {
          ...user,
          role: 'admin_TOTAL',
          permissions: []
        };
      }

      if (!user.permissions?.includes(value)) {
        return {
          ...user,
          role: 'custom',
          permissions: [...(user.permissions || []), value]
        };
      }
      return user;
    }));

    if (value === 'admin_TOTAL') {
      setUsers(prev => prev.map(user => {
        if (user.id === userId) return user;
        if (user.role === 'admin_TOTAL') {
          return {
            ...user,
            role: 'custom',
            permissions: ['requestlist']
          };
        }
        return user;
      }));
    }
  };

  // Remove permissão inline
  const handleRemovePermission = (userId, permId) => {
    setUsers(prev => prev.map(user => {
      if (user.id !== userId) return user;
      return {
        ...user,
        permissions: (user.permissions || []).filter(p => p !== permId)
      };
    }));
  };

  // Remove Controle Total inline
  const handleRemoveTotalAccess = (userId) => {
    const totalAdminsCount = users.filter(u => u.role === 'admin_TOTAL').length;
    if (totalAdminsCount <= 1) {
      alert('Aviso de Segurança: O sistema precisa de pelo menos 1 usuário com Controle Total.');
      return;
    }

    setUsers(prev => prev.map(user => {
      if (user.id !== userId) return user;
      return {
        ...user,
        role: 'custom',
        permissions: ['requestlist']
      };
    }));
  };

  // Salva alterações da linha (inline)
  const handleSave = async (user) => {
    setSavingId(user.id);
    setMessage(null);
    try {
      const userRef = doc(db, 'admins', user.id);
      await updateDoc(userRef, {
        name: user.name || user.email.split('@')[0],
        role: user.role,
        permissions: user.permissions || []
      });

      if (user.role === 'admin_TOTAL') {
        for (const u of users) {
          if (u.id !== user.id && u.role === 'admin_TOTAL') {
            const otherUserRef = doc(db, 'admins', u.id);
            await updateDoc(otherUserRef, {
              role: 'custom',
              permissions: ['requestlist']
            });
          }
        }
      }

      const permNames = user.role === 'admin_TOTAL'
        ? 'Controle Total'
        : (user.permissions || []).map(p => MODULES.find(m => m.id === p)?.name || p).join(', ') || 'Nenhum';

      await logAction('Alterar Permissões', {
        alvo_email: user.email,
        alvo_nome: user.name,
        novas_permissoes: permNames,
        nova_role: user.role
      });

      setMessage({ type: 'success', text: `Permissões de ${user.name} salvas com sucesso!` });
      fetchUsers();
    } catch (error) {
      console.error('Erro ao salvar permissões:', error);
      setMessage({ type: 'error', text: 'Ocorreu um erro ao salvar as alterações.' });
    } finally {
      setSavingId(null);
    }
  };

  // Exclui administrador
  const handleDeleteUser = async (userEmail, userName) => {
    if (currentUser && userEmail === currentUser.email) {
      window.alert('Você não pode excluir a si mesmo!');
      return;
    }
    const confirm = window.confirm(`Tem certeza que deseja remover o acesso administrativo de ${userName}? O usuário perderá todos os poderes no sistema.`);
    if (!confirm) return;

    try {
      await deleteDoc(doc(db, 'admins', userEmail));
      await logAction('Excluir Administrador', {
        alvo_email: userEmail,
        alvo_nome: userName,
        acao: 'Usuário removido da coleção admins'
      });
      setMessage({ type: 'success', text: `Acesso de ${userName} removido com sucesso.` });
      fetchUsers();
    } catch (error) {
      console.error('Erro ao excluir usuário:', error);
      setMessage({ type: 'error', text: 'Ocorreu um erro ao excluir o usuário.' });
    }
  };

  // Criação de Novo Administrador (Firebase Auth + Firestore)
  const handleCreateAdmin = async (e) => {
    e.preventDefault();
    setCreateLoading(true);
    setCreateError(null);

    const cleanEmail = formEmail.trim().toLowerCase();
    const cleanName = formName.trim() || cleanEmail.split('@')[0];
    const cleanPassword = formPassword.trim();

    if (!cleanEmail || !cleanPassword) {
      setCreateError('Preencha o e-mail e a senha.');
      setCreateLoading(false);
      return;
    }

    if (cleanPassword.length < 6) {
      setCreateError('A senha deve ter pelo menos 6 caracteres.');
      setCreateLoading(false);
      return;
    }

    try {
      // 1. Criar usuário no Firebase Authentication usando app secundário
      // para NÃO desconectar o Super Admin que está atualmente logado
      const SECONDARY_APP_NAME = 'AdminCreateWorker';
      let secondaryApp = getApps().find(a => a.name === SECONDARY_APP_NAME);
      if (!secondaryApp) {
        secondaryApp = initializeApp(firebaseConfig, SECONDARY_APP_NAME);
      }
      const secondaryAuth = getAuth(secondaryApp);

      try {
        await createUserWithEmailAndPassword(secondaryAuth, cleanEmail, cleanPassword);
        await signOut(secondaryAuth);
      } catch (authErr) {
        if (authErr.code === 'auth/email-already-in-use') {
          const existingInList = users.some(u => u.id === cleanEmail || u.email?.toLowerCase() === cleanEmail);
          if (existingInList) {
            setCreateError('Este e-mail já está cadastrado na lista de administradores.');
            setCreateLoading(false);
            return;
          }
          console.warn('Usuário já existe no Firebase Auth, vinculando à coleção admins...');
        } else if (authErr.code === 'auth/invalid-email') {
          setCreateError('Formato de e-mail inválido.');
          setCreateLoading(false);
          return;
        } else if (authErr.code === 'auth/weak-password') {
          setCreateError('Senha muito fraca. Digite pelo menos 6 caracteres.');
          setCreateLoading(false);
          return;
        } else {
          throw authErr;
        }
      }

      // 2. Criar documento na coleção 'admins'
      const newAdminDoc = {
        email: cleanEmail,
        name: cleanName,
        role: 'custom',
        permissions: formPermissions,
        createdAt: new Date().toISOString()
      };

      await setDoc(doc(db, 'admins', cleanEmail), newAdminDoc);

      const permNames = (formPermissions || [])
        .map(p => MODULES.find(m => m.id === p)?.name || p)
        .join(', ') || 'Nenhum';

      // 3. Registrar no Log de Auditoria
      await logAction('Cadastrar Administrador', {
        alvo_email: cleanEmail,
        alvo_nome: cleanName,
        role: 'custom',
        permissoes_iniciais: permNames
      });

      setMessage({
        type: 'success',
        text: `Administrador "${cleanName}" cadastrado com sucesso com as permissões configuradas!`
      });

      setShowCreateModal(false);
      setFormName('');
      setFormEmail('');
      setFormPassword('');
      setFormPermissions(['requestlist']);
      setShowFormPassword(false);
      await fetchUsers();
    } catch (err) {
      console.error('Erro ao cadastrar administrador:', err);
      setCreateError(err.message || 'Erro ao cadastrar administrador.');
    } finally {
      setCreateLoading(false);
    }
  };

  // Salvar alterações do Modal de Edição
  const handleSaveEditModal = async (e) => {
    e?.preventDefault();
    if (!editingUser) return;
    setEditLoading(true);
    setEditError(null);

    try {
      const userRef = doc(db, 'admins', editingUser.id);
      await updateDoc(userRef, {
        name: editingUser.name || editingUser.email.split('@')[0],
        role: editingUser.role,
        permissions: editingUser.permissions || []
      });

      if (editingUser.role === 'admin_TOTAL') {
        for (const u of users) {
          if (u.id !== editingUser.id && u.role === 'admin_TOTAL') {
            const otherUserRef = doc(db, 'admins', u.id);
            await updateDoc(otherUserRef, {
              role: 'custom',
              permissions: ['requestlist']
            });
          }
        }
      }

      const permNames = editingUser.role === 'admin_TOTAL'
        ? 'Controle Total'
        : (editingUser.permissions || []).map(p => MODULES.find(m => m.id === p)?.name || p).join(', ') || 'Nenhum';

      await logAction('Editar Administrador', {
        alvo_email: editingUser.email,
        alvo_nome: editingUser.name,
        novas_permissoes: permNames,
        nova_role: editingUser.role
      });

      setMessage({ type: 'success', text: `Dados de ${editingUser.name} atualizados com sucesso!` });
      setEditingUser(null);
      fetchUsers();
    } catch (error) {
      console.error('Erro ao atualizar usuário:', error);
      setEditError('Erro ao atualizar dados do administrador.');
    } finally {
      setEditLoading(false);
    }
  };

  // Alternar permissão no modal de edição
  const togglePermissionInEdit = (permId) => {
    if (!editingUser) return;
    const current = editingUser.permissions || [];
    const exists = current.includes(permId);
    setEditingUser({
      ...editingUser,
      permissions: exists
        ? current.filter(p => p !== permId)
        : [...current, permId]
    });
  };

  const handleSelectAllPermissionsInEdit = () => {
    if (!editingUser) return;
    setEditingUser({
      ...editingUser,
      permissions: MODULES.map(m => m.id)
    });
  };

  const handleClearAllPermissionsInEdit = () => {
    if (!editingUser) return;
    setEditingUser({
      ...editingUser,
      permissions: []
    });
  };

  if (loading) {
    return <div className={styles.centered}>Carregando administradores...</div>;
  }

  return (
    <div className={styles.container}>
      <Link to="/admin/admin" className={styles.btnBack} title="Sair do Módulo">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ width: '20px', height: '20px' }}>
          <path d="M19 12H5M12 19l-7-7 7-7" />
        </svg>
      </Link>

      <div className={styles.headerRow}>
        <div className={styles.headerText}>
          <h2 className={styles.title}>Gerência de Acessos</h2>
          <p className={styles.subtitle}>Gerencie os poderes e privilégios de acesso de cada administrador do sistema.</p>
        </div>
        <button
          className={styles.btnCreate}
          onClick={() => {
            setCreateError(null);
            setFormPermissions(['requestlist']);
            setShowCreateModal(true);
          }}
          title="Cadastrar Novo Administrador"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="12" y1="5" x2="12" y2="19"></line>
            <line x1="5" y1="12" x2="19" y2="12"></line>
          </svg>
          + Cadastrar
        </button>
      </div>

      {message && (
        <div className={`${styles.alert} ${styles[message.type]}`}>
          {message.text}
        </div>
      )}

      <table className={styles.table}>
        <thead>
          <tr>
            <th>Nome</th>
            <th>E-mail</th>
            <th>Poderes/Permissões</th>
            <th>Ações</th>
          </tr>
        </thead>
        <tbody>
          {users.map((user) => {
            const isTotalAdmin = user.role === 'admin_TOTAL';
            const hasAnyTotalAdmin = !!currentTotalAdmin;
            const canGiveTotalAdmin = !hasAnyTotalAdmin || (hasAnyTotalAdmin && currentTotalAdmin.id === user.id);

            return (
              <tr key={user.id}>
                <td className={styles.userName}>{user.name}</td>
                <td className={styles.userEmail}>{user.email}</td>
                <td>
                  <div className={styles.permissionsContainer}>
                    {/* Tags Atuais */}
                    <div className={styles.tagsContainer}>
                      {isTotalAdmin ? (
                        <span className={styles.tagTotal}>
                          👑 Controle Total
                          <button
                            type="button"
                            className={styles.removeTagBtn}
                            onClick={() => handleRemoveTotalAccess(user.id)}
                            title="Remover Controle Total"
                          >
                            ×
                          </button>
                        </span>
                      ) : (
                        (user.permissions || []).map((permId) => {
                          const mod = MODULES.find(m => m.id === permId);
                          return (
                            <span key={permId} className={styles.tagCustom}>
                              {mod ? mod.name : permId}
                              <button
                                type="button"
                                className={styles.removeTagBtn}
                                onClick={() => handleRemovePermission(user.id, permId)}
                                title="Remover Permissão"
                              >
                                ×
                              </button>
                            </span>
                          );
                        })
                      )}
                    </div>

                    {/* Dropdown de Seleção de Permissões */}
                    {!isTotalAdmin && (
                      <select
                        className={styles.selectPermissions}
                        value=""
                        onChange={(e) => handleAddPermission(user.id, e.target.value)}
                      >
                        <option value="" disabled>+ Adicionar poder...</option>
                        {canGiveTotalAdmin && (
                          <option value="admin_TOTAL" className={styles.optionTotal}>
                            👑 Controle Total
                          </option>
                        )}
                        {MODULES
                          .filter(mod => !(user.permissions || []).includes(mod.id))
                          .map(mod => (
                            <option key={mod.id} value={mod.id}>
                              {mod.name}
                            </option>
                          ))
                        }
                      </select>
                    )}
                  </div>
                </td>
                <td className={styles.actionCell}>
                  <button
                    className={styles.saveBtn}
                    onClick={() => handleSave(user)}
                    disabled={savingId === user.id}
                    title="Salvar alterações de poderes"
                  >
                    {savingId === user.id ? 'Salvando...' : 'Salvar'}
                  </button>
                  <button
                    className={styles.editBtn}
                    onClick={() => {
                      setEditError(null);
                      setEditingUser({ ...user });
                    }}
                    title="Editar Administrador"
                  >
                    Editar
                  </button>
                  <button
                    className={styles.deleteBtn}
                    onClick={() => handleDeleteUser(user.email, user.name)}
                    disabled={savingId === user.id || (currentUser && user.email === currentUser.email)}
                    title={currentUser && user.email === currentUser.email ? 'Você não pode excluir seu próprio acesso' : 'Excluir Administrador'}
                  >
                    Excluir
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* --- MODAL DE CADASTRO (+ Cadastrar) --- */}
      {showCreateModal && (
        <div className={styles.modalOverlay} onClick={() => !createLoading && setShowCreateModal(false)}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <div>
                <h3 className={styles.modalTitle}>Cadastrar Novo Administrador</h3>
                <p className={styles.modalSubtitle}>Crie o usuário no Authentication e configure os acessos.</p>
              </div>
              <button
                type="button"
                className={styles.closeModalBtn}
                onClick={() => !createLoading && setShowCreateModal(false)}
              >
                ×
              </button>
            </div>

            <form onSubmit={handleCreateAdmin} className={styles.modalForm}>
              <div className={styles.modalBody}>
                {createError && (
                  <div className={styles.modalError}>
                    {createError}
                  </div>
                )}

                <div className={styles.formGroup}>
                  <label className={styles.label}>Nome</label>
                  <input
                    type="text"
                    className={styles.input}
                    placeholder="Ex: João da Silva"
                    value={formName}
                    onChange={(e) => setFormName(e.target.value)}
                  />
                  <div className={styles.helperText}>Nome identificador do funcionário ou gerente.</div>
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.label}>E-mail *</label>
                  <input
                    type="email"
                    required
                    className={styles.input}
                    placeholder="exemplo@restaurante.com"
                    value={formEmail}
                    onChange={(e) => setFormEmail(e.target.value)}
                  />
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.label}>Senha *</label>
                  <div className={styles.passwordWrapper}>
                    <input
                      type={showFormPassword ? 'text' : 'password'}
                      required
                      className={styles.input}
                      placeholder="Mínimo 6 caracteres"
                      value={formPassword}
                      onChange={(e) => setFormPassword(e.target.value)}
                    />
                    <button
                      type="button"
                      className={styles.togglePasswordBtn}
                      onClick={() => setShowFormPassword(!showFormPassword)}
                      title={showFormPassword ? 'Ocultar senha' : 'Ver senha'}
                    >
                      {showFormPassword ? (
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                          <line x1="1" y1="1" x2="23" y2="23"></line>
                        </svg>
                      ) : (
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                          <circle cx="12" cy="12" r="3"></circle>
                        </svg>
                      )}
                    </button>
                  </div>
                  <div className={styles.helperText}>A senha de acesso ao painel do sistema.</div>
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.label}>Permissões de Acesso</label>
                  <div className={styles.quickActions}>
                    <button
                      type="button"
                      className={styles.quickActionBtn}
                      onClick={handleSelectAllPermissionsInCreate}
                    >
                      ✓ Selecionar Todas
                    </button>
                    <button
                      type="button"
                      className={styles.quickActionBtn}
                      onClick={handleClearAllPermissionsInCreate}
                    >
                      ✕ Limpar Todas
                    </button>
                  </div>

                  <div className={styles.permissionsGrid}>
                    {MODULES.map((mod) => {
                      const checked = (formPermissions || []).includes(mod.id);
                      return (
                        <label key={mod.id} className={styles.checkboxLabel}>
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => togglePermissionInCreate(mod.id)}
                          />
                          <span>{mod.name}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              </div>

              <div className={styles.modalFooter}>
                <button
                  type="button"
                  className={styles.btnCancel}
                  onClick={() => setShowCreateModal(false)}
                  disabled={createLoading}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className={styles.btnSubmit}
                  disabled={createLoading}
                >
                  {createLoading ? 'Cadastrando...' : 'Cadastrar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* --- MODAL DE EDIÇÃO (Editar) --- */}
      {editingUser && (
        <div className={styles.modalOverlay} onClick={() => !editLoading && setEditingUser(null)}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <div>
                <h3 className={styles.modalTitle}>Editar Administrador</h3>
                <p className={styles.modalSubtitle}>{editingUser.email}</p>
              </div>
              <button
                type="button"
                className={styles.closeModalBtn}
                onClick={() => !editLoading && setEditingUser(null)}
              >
                ×
              </button>
            </div>

            <form onSubmit={handleSaveEditModal} className={styles.modalForm}>
              <div className={styles.modalBody}>
                {editError && (
                  <div className={styles.modalError}>
                    {editError}
                  </div>
                )}

                <div className={styles.formGroup}>
                  <label className={styles.label}>Nome</label>
                  <input
                    type="text"
                    className={styles.input}
                    value={editingUser.name || ''}
                    onChange={(e) => setEditingUser({ ...editingUser, name: e.target.value })}
                  />
                </div>

                {editingUser.role === 'admin_TOTAL' ? (
                  <div className={styles.formGroup}>
                    <label className={styles.label}>Poderes</label>
                    <div className={styles.tagTotal} style={{ display: 'inline-flex', padding: '6px 12px' }}>
                      👑 Este usuário possui Controle Total (Super Admin)
                    </div>
                  </div>
                ) : (
                  <div className={styles.formGroup}>
                    <label className={styles.label}>Permissões de Acesso</label>
                    <div className={styles.quickActions}>
                      <button
                        type="button"
                        className={styles.quickActionBtn}
                        onClick={handleSelectAllPermissionsInEdit}
                      >
                        ✓ Selecionar Todas
                      </button>
                      <button
                        type="button"
                        className={styles.quickActionBtn}
                        onClick={handleClearAllPermissionsInEdit}
                      >
                        ✕ Limpar Todas
                      </button>
                    </div>

                    <div className={styles.permissionsGrid}>
                      {MODULES.map((mod) => {
                        const checked = (editingUser.permissions || []).includes(mod.id);
                        return (
                          <label key={mod.id} className={styles.checkboxLabel}>
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => togglePermissionInEdit(mod.id)}
                            />
                            <span>{mod.name}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              <div className={styles.modalFooter}>
                <button
                  type="button"
                  className={styles.btnCancel}
                  onClick={() => setEditingUser(null)}
                  disabled={editLoading}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className={styles.btnSubmit}
                  disabled={editLoading}
                >
                  {editLoading ? 'Salvando...' : 'Salvar Alterações'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
